import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { match } from 'ts-pattern'

import Header from '@/components-latest/layout/Header'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import BillingCycleDetails from '@/components-latest/ui/BillingPage/BillingCycleDetails'
import BillingCycleFilter from '@/components-latest/ui/BillingPage/BillingCycleFilter'
import BillingCyclesList from '@/components-latest/ui/BillingPage/BillingCyclesList'
import EmptyBillingPage from '@/components-latest/ui/BillingPage/EmptyBillingPage'
import WalletCard from '@/components-latest/ui/BillingPage/WalletCard'
import {
  DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE,
  WalletTransactionsPageSize
} from '@/components-latest/ui/BillingPage/WalletTransactions/constants'
import type { WalletTransaction } from '@/components-latest/ui/BillingPage/WalletTransactions/types'
import WalletTransactions from '@/components-latest/ui/BillingPage/WalletTransactions/WalletTransactions'
import { selectToken } from '@/ducks/auth/auth.selectors'
import {
  useLazyFetchBillingCyclesQuery,
  useLazyFetchWalletDetailsQuery,
  useLazyFetchWalletTransactionsQuery
} from '@/ducks/billing/api/billing.api'
import {
  IBillingCycle,
  IGetBillingCyclesResponse,
  IWalletTransactionApiItem
} from '@/ducks/billing/billing.types'
import { resolveWalletTransactionClient } from '@/ducks/billing/billing.wallet-transactions'
import {
  selectCurrencyCode,
  selectPhoneCode,
  selectStoreCashCoStatus,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'

import Styles from './BillingPage.module.scss'

const WALLET_FLAG_KEY_KEBAB = 'lmd-store-enable-restaurant-wallet-web'
const WALLET_CURRENCY_CODE = 'DA'

const formatWalletDateTime = (
  value: string,
  locale: string,
  todayLabel: string
) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()

  const timePart = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  })

  if (isToday) {
    return `${todayLabel}, ${timePart}`
  }

  const datePart = date.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short'
  })
  return `${datePart}, ${timePart}`
}

const mapWalletStatus = (
  status: string | undefined
): WalletTransaction['status'] => {
  const normalizedStatus = String(status || '').toUpperCase()
  if (normalizedStatus === 'CONFIRMED' || normalizedStatus === 'DONE') {
    return 'done'
  }
  if (normalizedStatus === 'REFUND' || normalizedStatus === 'REFUNDED') {
    return 'refund'
  }
  return 'pending'
}

const mapWalletType = (transactionType: string | undefined) => {
  const normalizedType = String(transactionType || '').toUpperCase()
  if (normalizedType.includes('TOP_UP')) return 'Top up'
  if (normalizedType.includes('CASH_OUT')) return 'Cash out'
  if (normalizedType.includes('ORDER')) return 'Order'
  return 'Order'
}

const BillingPage = () => {
  const { t, i18n } = useTranslation()

  const storeId = useSelector(selectStoreId)
  const isStoreUsingCashCo = useSelector(selectStoreCashCoStatus)
  const authToken = useSelector(selectToken)

  const flags = useFlags()
  const { lmd_store_webapp_FR_Billing } = flags
  const isRestaurantWalletEnabled = Boolean(flags?.[WALLET_FLAG_KEY_KEBAB])

  const phoneCode = useSelector(selectPhoneCode)
  const currencyCode = useSelector(selectCurrencyCode) || ''

  const [getBillingCycles, { isFetching, data }] =
    useLazyFetchBillingCyclesQuery()
  const [
    getWalletTransactions,
    {
      data: walletTransactionsResponse,
      isFetching: isWalletTransactionsFetching
    }
  ] = useLazyFetchWalletTransactionsQuery()
  const [getWalletDetails, { data: walletDetailsResponse }] =
    useLazyFetchWalletDetailsQuery()

  useEffect(() => {
    if (!storeId || !isStoreUsingCashCo) return
    getBillingCycles({ storeId })
  }, [storeId, getBillingCycles, isStoreUsingCashCo])

  useEffect(() => {
    if (!storeId || !isStoreUsingCashCo || !isRestaurantWalletEnabled) return
    getWalletDetails({ token: authToken || undefined })
  }, [
    storeId,
    authToken,
    getWalletDetails,
    isStoreUsingCashCo,
    isRestaurantWalletEnabled
  ])

  const [activeCycle, setActiveCycle] = useState<IBillingCycle | null>(null)
  const [activeCycleType, setActiveCycleType] = useState<
    'paid' | 'unpaid' | null
  >(null)
  const [isWalletTransactionsOpen, setIsWalletTransactionsOpen] =
    useState(false)
  const [walletTxPage, setWalletTxPage] = useState(0)
  const [walletTxLimit, setWalletTxLimit] = useState<WalletTransactionsPageSize>(
    DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE
  )
  const [walletBrandFilter, setWalletBrandFilter] = useState<
    'kool' | 'yassir' | undefined
  >(undefined)
  const [cycleFilter, setCycleFilter] = useState<
    'paid' | 'unpaid' | 'unreceived'
  >('unpaid')

  const isPaidStatus = (paidStatus: unknown) => {
    const normalizedStatus = String(paidStatus).toLowerCase()
    return (
      normalizedStatus === '1' ||
      normalizedStatus === 'paid' ||
      normalizedStatus === 'true'
    )
  }

  const normalizedCycles = useMemo(() => {
    if (!data) {
      return {
        paid: [] as IBillingCycle[],
        unpaid: [] as IBillingCycle[],
        unreceived: [] as IBillingCycle[]
      }
    }

    const { paid_billing = [], unpaid_billing = [] } =
      data as IGetBillingCyclesResponse
    const mergedCycles = [...unpaid_billing, ...paid_billing]

    const uniqueByCycle = Array.from(
      new Map(mergedCycles.map(cycle => [cycle.billing_cycle, cycle])).values()
    )

    return {
      paid: uniqueByCycle.filter(cycle => isPaidStatus(cycle.paid_status)),
      unpaid: uniqueByCycle.filter(cycle => !isPaidStatus(cycle.paid_status)),
      unreceived: uniqueByCycle.filter(
        cycle =>
          !isPaidStatus(cycle.paid_status) &&
          Number(cycle?.billings?.final_admin_earnings || 0) > 0
      )
    }
  }, [data])

  const openCycleDetailsCreator = (
    cycle: IBillingCycle | null,
    type: 'paid' | 'unpaid'
  ) => {
    if (!cycle || !type) {
      setActiveCycle(null)
      setActiveCycleType(null)
      return
    }
    setActiveCycle(cycle)
    setActiveCycleType(type)
  }

  const walletTxOffset = walletTxPage * walletTxLimit

  useEffect(() => {
    if (!isWalletTransactionsOpen) return
    getWalletTransactions({
      limit: walletTxLimit,
      offset: walletTxOffset,
      ...(walletBrandFilter ? { brand: walletBrandFilter } : {})
    })
  }, [
    isWalletTransactionsOpen,
    walletTxLimit,
    walletTxOffset,
    walletBrandFilter,
    getWalletTransactions
  ])

  useEffect(() => {
    if (!isWalletTransactionsOpen || !isRestaurantWalletEnabled) return
    getWalletDetails({ token: authToken || undefined })
  }, [
    isWalletTransactionsOpen,
    isRestaurantWalletEnabled,
    authToken,
    getWalletDetails
  ])

  useEffect(() => {
    if (!data) return
    const {
      unpaid: unpaidBilling,
      paid: paidBilling,
      unreceived: unreceivedBilling
    } = normalizedCycles
    if (
      cycleFilter === 'unpaid' &&
      unpaidBilling.length === 0 &&
      paidBilling.length
    ) {
      setCycleFilter('paid')
      return
    }

    if (
      cycleFilter === 'paid' &&
      paidBilling.length === 0 &&
      unpaidBilling.length
    ) {
      setCycleFilter('unpaid')
    }

    if (cycleFilter === 'unreceived' && unreceivedBilling.length === 0) {
      if (unpaidBilling.length) {
        setCycleFilter('unpaid')
        return
      }
      if (paidBilling.length) {
        setCycleFilter('paid')
      }
    }
  }, [data, cycleFilter, normalizedCycles])

  const storeWalletAmount = useMemo(() => {
    if (!data) return 0

    const sortedCycles = [
      ...normalizedCycles.unpaid,
      ...normalizedCycles.paid
    ].sort(
      (cycleA, cycleB) =>
        (cycleB?.details?.end_time || 0) - (cycleA?.details?.end_time || 0)
    )

    return sortedCycles[0]?.billings?.last_billings_credit || 0
  }, [data, normalizedCycles])

  const walletAmountFromApi = walletDetailsResponse?.balance
  const walletCurrencyFromApi = walletDetailsResponse?.currency
  const resolvedWalletAmount =
    typeof walletAmountFromApi === 'number' &&
    Number.isFinite(walletAmountFromApi)
      ? walletAmountFromApi
      : storeWalletAmount
  const resolvedWalletCurrency =
    walletCurrencyFromApi || currencyCode || WALLET_CURRENCY_CODE

  const walletTransactions = useMemo<WalletTransaction[]>(() => {
    const items = walletTransactionsResponse?.data || []
    const currentLocale = i18n.resolvedLanguage || i18n.language || 'en'
    const todayLabel = t('billing.wallet.today')

    return items.map((transaction: IWalletTransactionApiItem) => {
      const rawAmount = transaction.amount_with_fee ?? transaction.amount ?? 0
      const signedAmount =
        String(transaction.entry_type).toUpperCase() === 'DEBIT'
          ? -Math.abs(rawAmount)
          : Math.abs(rawAmount)

      const { clientName, clientAvatar } =
        resolveWalletTransactionClient(transaction)

      return {
        id: transaction.transaction_id,
        type: mapWalletType(transaction.transaction_type),
        dateTime: formatWalletDateTime(
          transaction.transaction_date_time,
          currentLocale,
          todayLabel
        ),
        clientName,
        clientAvatar,
        orderId: transaction.order_id || '-',
        status: mapWalletStatus(transaction.status),
        amount: signedAmount,
        currency: transaction.currency || WALLET_CURRENCY_CODE,
        brand: (transaction.brand as 'kool' | 'yassir') ?? null
      }
    })
  }, [i18n.language, i18n.resolvedLanguage, t, walletTransactionsResponse])

  if (!isStoreUsingCashCo) return null

  if (!lmd_store_webapp_FR_Billing && phoneCode === '+33') return null

  return (
    <div className={Styles.page} data-testid="billing-page">
      <Header
        onGoBack={
          activeCycle
            ? () => {
                setActiveCycle(null)
              }
            : undefined
        }
        title={activeCycle ? t('billing.page-title-details') : ''}
      />
      <div className={Styles.content}>
        {match({ isFetching, data, activeCycle })
          .when(
            ({ data, isFetching }) => !data || isFetching,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .when(
            () =>
              !isRestaurantWalletEnabled &&
              normalizedCycles.paid.length === 0 &&
              normalizedCycles.unpaid.length === 0,
            () => <EmptyBillingPage />
          )
          .when(
            ({ activeCycle }) => !activeCycle,
            () => {
              if (isWalletTransactionsOpen) {
                return (
                  <WalletTransactions
                    onBack={() => {
                      setIsWalletTransactionsOpen(false)
                      setWalletTxPage(0)
                      setWalletBrandFilter(undefined)
                    }}
                    transactions={walletTransactions}
                    isLoading={isWalletTransactionsFetching}
                    balanceAmount={resolvedWalletAmount}
                    currencyCode={resolvedWalletCurrency}
                    page={walletTxPage}
                    limit={walletTxLimit}
                    offset={walletTxOffset}
                    total={walletTransactionsResponse?.pagination?.total}
                    onPageChange={setWalletTxPage}
                    onLimitChange={limit => {
                      setWalletTxLimit(limit)
                      setWalletTxPage(0)
                    }}
                    brandFilter={walletBrandFilter}
                    onBrandFilterChange={brand => {
                      setWalletBrandFilter(brand)
                      setWalletTxPage(0)
                    }}
                    formatDateTime={value =>
                      formatWalletDateTime(
                        value,
                        i18n.resolvedLanguage || i18n.language || 'en',
                        t('billing.wallet.today')
                      )
                    }
                  />
                )
              }

              const {
                paid: paidBilling,
                unpaid: unpaidBilling,
                unreceived: unreceivedBilling
              } = normalizedCycles
              const hasUnpaidCycles = unpaidBilling.length > 0
              const hasPaidCycles = paidBilling.length > 0
              const hasUnreceivedCycles = unreceivedBilling.length > 0

              const cyclesToDisplay = match(cycleFilter)
                .with('unpaid', () => unpaidBilling)
                .with('paid', () => paidBilling)
                .with('unreceived', () => unreceivedBilling)
                .exhaustive()

              return (
                <ContentBlock className={Styles.billingCard}>
                  <div className={Styles.titleRow}>
                    <div className={Styles.titleContent}>
                      <Text variant="nextBody" className={Styles.cardTitle}>
                        {t('billing.page-title')}
                      </Text>
                    </div>
                  </div>
                  <div
                    className={clsx(Styles.billingSections, {
                      [Styles.billingSectionsWithoutWallet]:
                        !isRestaurantWalletEnabled
                    })}>
                    {isRestaurantWalletEnabled && (
                      <WalletCard
                        amount={resolvedWalletAmount}
                        currencyCode={resolvedWalletCurrency}
                        onOpen={() => {
                          setIsWalletTransactionsOpen(true)
                        }}
                      />
                    )}
                    <BillingCycleFilter
                      activeFilter={cycleFilter}
                      hasPaidCycles={hasPaidCycles}
                      hasUnpaidCycles={hasUnpaidCycles}
                      hasUnreceivedCycles={hasUnreceivedCycles}
                      onFilterChange={setCycleFilter}
                      noTopPadding={!isRestaurantWalletEnabled}
                    />
                    <BillingCyclesList
                      onDetailsClick={openCycleDetailsCreator}
                      cycles={cyclesToDisplay}
                      type={cycleFilter}
                      showTitle={false}
                    />
                  </div>
                </ContentBlock>
              )
            }
          )
          .when(
            ({ activeCycle }) => activeCycle,
            () => {
              return (
                <BillingCycleDetails
                  cycle={activeCycle}
                  type={activeCycleType}
                />
              )
            }
          )
          .otherwise(() => null)}
      </div>
    </div>
  )
}

export default BillingPage
